/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useSearchParams } from '@remix-run/react'
import { useEffect, type Dispatch } from 'react'
import { useTranslation } from 'react-i18next'
import { TextareaChatField } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ClientOnly } from '#app/hooks/use-client-only.tsx'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'
import { type Action } from '#types/chat/index.ts'
import { ErrorMessage } from '../../error-message.tsx'
import { FormFooter } from '../../form-footer.tsx'
import PaymentModal from '../../payment-modal/index.tsx'
import { getMessageBasedOnType } from '../utils/getMessageBasedOnType.ts'
import { onSubmit } from '../utils/onSubmit.ts'

interface MessageFetcherFormProps {
	isEmptyChat: boolean
	error: any
	isValid: boolean
	isSubmitting: boolean
	textareaRef: React.RefObject<HTMLTextAreaElement>
	eventSourceRef: any
	register: any
	trigger: Function
	setValue: Function
	showEmptyChat: boolean
	dispatch: Dispatch<Action>
	handleSubmit: Function
	messageFetcher: any
	conversationId: string
	resetField: Function
	onInvalid: Function
}

export const MessageFetcherForm: React.FC<MessageFetcherFormProps> = ({
	error,
	isValid,
	isSubmitting,
	textareaRef,
	eventSourceRef,
	register,
	trigger,
	setValue,
	showEmptyChat,
	dispatch,
	handleSubmit,
	messageFetcher,
	conversationId,
	resetField,
	onInvalid,
}) => {
	const { t } = useTranslation('chat')
	const [searchParams] = useSearchParams()
	const typeOfMessage = searchParams.get('typeOfMessage')
	const navigate = useNavigate()
	const coinsLeft = useGlobalChatStore(state => state.coinsLeft)
	const typeOfView = useGlobalChatStore(state => state.typeOfView)

	useEffect(() => {
		if (typeOfMessage && coinsLeft > 0) {
			const message = getMessageBasedOnType(typeOfMessage, t)

			if (!message) return

			triggerFormSubmission(message).then(() => {
				navigate(window.location.pathname)
			})
		}
	}, [typeOfMessage, coinsLeft])

	async function triggerFormSubmission(message: string) {
		setValue('wiserBotMessage', message)
		onSubmit({
			currentFormContext: { wiserBotMessage: message },
			showEmptyChat,
			textareaRef,
			dispatch,
			messageFetcher,
			conversationId,
			typeOfView,
		})

		resetField('wiserBotMessage')
	}

	return (
		<messageFetcher.Form
			method="POST"
			onSubmit={handleSubmit(
				async (currentFormContext: { wiserBotMessage: string }) => {
					onSubmit({
						currentFormContext,
						showEmptyChat,
						textareaRef,
						dispatch,
						messageFetcher,
						conversationId,
						typeOfView,
					})

					resetField('wiserBotMessage')
				},
				onInvalid,
			)}
			className="mx-4 gap-3 lg:mx-auto lg:w-full lg:max-w-2xl xl:max-w-3xl"
		>
			<ErrorMessage error={error} />
			{coinsLeft <= 0 ? (
				<ClientOnly>
					{() => (
						<PaymentModal
							customButton={
								<Button className="w-full py-7">{t('buyMoreCoins')}</Button>
							}
						/>
					)}
				</ClientOnly>
			) : (
				<TextareaChatField
					isValid={isValid}
					textareaRef={textareaRef}
					eventSourceRef={eventSourceRef as any}
					textareaProps={{
						maxLength: 20_000,
						...register('wiserBotMessage'),
						autoFocus: true,
						disabled: isSubmitting && !isValid,
						className: 'flex grow text-[1rem] w-full pl-5 mt-5',
						placeholder: t('messageWiserbot'),
						onChange: e => {
							trigger()
							setValue('wiserBotMessage', e.target.value)
						},
						onKeyPress: (e: any) => {
							if (e.key === 'Enter' && !e.shiftKey && isValid) {
								e.preventDefault()
								onSubmit({
									currentFormContext: { wiserBotMessage: e.target.value },
									showEmptyChat,
									textareaRef,
									dispatch,
									messageFetcher,
									conversationId,
									typeOfView,
								})

								resetField('wiserBotMessage')
							}
						},
					}}
				/>
			)}
			<FormFooter />
		</messageFetcher.Form>
	)
}
